/**
 * Shared TypeScript types and Zod schemas for replay system
 *
 * This file provides:
 * 1. Runtime validation with Zod schemas
 * 2. Compile-time type safety with TypeScript types
 * 3. Shared types between client and server to prevent API mismatches
 *
 * Benefits:
 * - Catches schema mismatches at compile time
 * - Prevents sending non-existent fields (e.g., time_elapsed_ms)
 * - Enforces correct types (e.g., game_state: number, not string)
 * - Runtime validation ensures data integrity
 */

import { z } from 'zod';

// =============================================================================
// Game State Constants
// =============================================================================

export const GAME_STATE = {
  NOT_YET_BEGUN: 0,
  IN_PROGRESS: 1,
  COMPLETE: 2
} as const;

export type GameStateValue = typeof GAME_STATE[keyof typeof GAME_STATE];

// =============================================================================
// Twitter User Schema
// =============================================================================

export const TwitterUserSchema = z.object({
  screen_name: z.string(),
  name: z.string().optional(),
  profile_image_url_https: z.string().optional(),
  user_id: z.string().optional(),
  id_str: z.string().optional()
});

export type TwitterUser = z.infer<typeof TwitterUserSchema>;

// =============================================================================
// Replay Session Schema (Database Schema)
// =============================================================================

/**
 * Full replay session as stored in database
 * This matches the mfersbeheaded_replay_sessions table schema
 */
export const ReplaySessionSchema = z.object({
  // Required fields
  id: z.string().uuid(),
  level_id: z.string(),
  level_name: z.string(),

  // Player info
  player_name: z.string().nullable(),
  twitter_user: TwitterUserSchema.nullable(),

  // Game state - MUST be a number (0, 1, or 2)
  game_state: z.union([
    z.literal(0),
    z.literal(1),
    z.literal(2)
  ]).describe('Game state: 0=NOT_YET_BEGUN, 1=IN_PROGRESS, 2=COMPLETE'),

  // Final stats
  final_score: z.number().nullable(),
  final_moves: z.number().nullable(),

  // Timestamps
  created_at: z.string().datetime().optional(),
  duration_ms: z.number().nullable().optional(),
});

export type ReplaySession = z.infer<typeof ReplaySessionSchema>;

// =============================================================================
// API Request Schemas
// =============================================================================

// Environment check for strict validation mode
// In test/staging, we use strict mode to catch schema mismatches early
// In production, we use passthrough to allow backwards compatibility
const isStrictMode = typeof process !== 'undefined' &&
  (process.env.NODE_ENV === 'test' ||
   process.env.NODE_ENV === 'development' ||
   (typeof window !== 'undefined' && window.location?.hostname?.includes('staging')));

/**
 * Schema for creating a new replay session
 * Only includes fields that should be sent during session creation
 */
const CreateSessionRequestBaseSchema = z.object({
  id: z.string().uuid(),
  level_id: z.string(),
  level_name: z.string(),
  player_name: z.string().optional(),
  user_id: z.string().optional(), // New auth system user ID
  twitter_user: TwitterUserSchema.optional(),
  game_state: z.union([z.literal(0), z.literal(1), z.literal(2)]),
});

// Use strict mode in test/staging to catch unknown fields, passthrough in prod
export const CreateSessionRequestSchema = isStrictMode
  ? CreateSessionRequestBaseSchema.strict()
  : CreateSessionRequestBaseSchema.passthrough();

export type CreateSessionRequest = z.infer<typeof CreateSessionRequestBaseSchema>;

/**
 * Schema for updating a replay session
 * Only includes fields that are allowed to be updated
 *
 * This matches the allowedFields whitelist in supabaseRepository.js:156
 */
const UpdateSessionRequestBaseSchema = z.object({
  game_state: z.union([z.literal(0), z.literal(1), z.literal(2)]).optional(),
  final_score: z.number().optional(),
  final_moves: z.number().optional(),
  duration_ms: z.number().optional(),
  highest_tile: z.number().optional(),
  player_name: z.string().optional(),
  user_id: z.string().optional(), // New auth system user ID
  twitter_user: TwitterUserSchema.optional()
});

// Use strict mode in test/staging to catch unknown fields
export const UpdateSessionRequestSchema = isStrictMode
  ? UpdateSessionRequestBaseSchema.strict()
  : UpdateSessionRequestBaseSchema.passthrough();

export type UpdateSessionRequest = z.infer<typeof UpdateSessionRequestSchema>;

// =============================================================================
// Event Type Schema
// =============================================================================

export const EventTypeSchema = z.enum([
  'BOARD_INIT',
  'BOARD_STATE',
  'SWAP',
  'SCORE_UPDATE',
  'POWER_UP',
  'GAME_STATE',
  'BARREL_PLACE',
  'CLEAR',      // NEW: Records cells that were cleared
  'GRAVITY',    // NEW: Records piece movements from gravity
  'FILL'        // NEW: Records new pieces added to board
]);

export type EventType = z.infer<typeof EventTypeSchema>;

// =============================================================================
// Event Data Schemas
// =============================================================================

export const StateChangeSchema = z.tuple([
  z.number(), // row
  z.number(), // col
  z.number()  // value
]);

export type StateChange = z.infer<typeof StateChangeSchema>;

export const BoardInitDataSchema = z.object({
  changes: z.array(StateChangeSchema),
  board_size: z.number()
});

export const BoardStateDataSchema = z.object({
  changes: z.array(StateChangeSchema),
  context: z.enum(['sweep', 'gravity', 'fill']).optional()
});

export const SwapDataSchema = z.object({
  from: z.tuple([z.number(), z.number()]),
  to: z.tuple([z.number(), z.number()]),
  from_value: z.number().optional(), // NEW: Value in source cell before swap
  to_value: z.number().optional(),   // NEW: Value in target cell before swap
  valid: z.boolean()
});

export const ScoreUpdateDataSchema = z.object({
  score: z.number(),
  delta: z.number(),
  reason: z.enum(['match', 'power_up', 'cascade'])
});

export const PowerUpDataSchema = z.object({
  position: z.tuple([z.number(), z.number()]),
  pieces_cleared: z.number()
});

export const GameStateDataSchema = z.object({
  state: z.union([z.literal(0), z.literal(1), z.literal(2)]),
  moves_used: z.number().optional(),
  time_elapsed_ms: z.number().optional()
});

export const BarrelPlaceDataSchema = z.object({
  changes: z.array(StateChangeSchema)
});

// NEW: Clear event data schema
export const ClearDataSchema = z.object({
  cleared: z.array(StateChangeSchema), // [row, col, oldValue] for each cleared cell
  reason: z.enum(['match', 'power_up', 'cascade']).optional()
});

// NEW: Gravity event data schema
export const GravityMovementSchema = z.object({
  from: z.tuple([z.number(), z.number()]), // [row, col] source
  to: z.tuple([z.number(), z.number()]),   // [row, col] destination
  value: z.number()                        // piece value that moved
});

export const GravityDataSchema = z.object({
  movements: z.array(GravityMovementSchema)
});

// NEW: Fill event data schema
export const FillDataSchema = z.object({
  new_pieces: z.array(StateChangeSchema) // [row, col, newValue] for each filled cell
});

// Union of all event data types
export const EventDataSchema = z.union([
  BoardInitDataSchema,
  BoardStateDataSchema,
  SwapDataSchema,
  ScoreUpdateDataSchema,
  PowerUpDataSchema,
  GameStateDataSchema,
  BarrelPlaceDataSchema,
  ClearDataSchema,
  GravityDataSchema,
  FillDataSchema
]);

export type EventData = z.infer<typeof EventDataSchema>;

// =============================================================================
// Replay Event Schema
// =============================================================================

export const ReplayEventSchema = z.object({
  session_id: z.string().uuid().optional(), // Optional when sending, added by server
  sequence: z.number().int().nonnegative().optional(), // Optional when sending, calculated by server
  timestamp_ms: z.number().nonnegative(),
  event_type: EventTypeSchema,
  data: EventDataSchema
});

export type ReplayEvent = z.infer<typeof ReplayEventSchema>;

// =============================================================================
// API Response Schemas
// =============================================================================

export const ReplaySessionResponseSchema = z.object({
  session: ReplaySessionSchema,
  events: z.array(ReplayEventSchema)
});

export type ReplaySessionResponse = z.infer<typeof ReplaySessionResponseSchema>;

export const PaginationSchema = z.object({
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  total: z.number().int().nonnegative(),
  totalPages: z.number().int().nonnegative(),
  hasNext: z.boolean(),
  hasPrev: z.boolean()
});

export const ReplayListResponseSchema = z.object({
  sessions: z.array(ReplaySessionSchema),
  pagination: PaginationSchema,
  sort: z.object({
    sortBy: z.enum(['score', 'date']),
    sortOrder: z.enum(['asc', 'desc'])
  })
});

export type ReplayListResponse = z.infer<typeof ReplayListResponseSchema>;

// =============================================================================
// Validation Helpers
// =============================================================================

/**
 * Validates and parses data with Zod schema
 * Returns { success: true, data } or { success: false, errors }
 */
export function validateData<T>(
  schema: z.ZodSchema<T>,
  data: unknown
): { success: true; data: T } | { success: false; errors: z.ZodError } {
  const result = schema.safeParse(data);
  if (result.success) {
    return { success: true, data: result.data };
  } else {
    return { success: false, errors: result.error };
  }
}

/**
 * Type guard to check if game state is complete
 */
export function isGameComplete(session: ReplaySession): boolean {
  return session.game_state === GAME_STATE.COMPLETE;
}

/**
 * Type guard to check if game is in progress
 */
export function isGameInProgress(session: ReplaySession): boolean {
  return session.game_state === GAME_STATE.IN_PROGRESS;
}
